"use client";

import { AlertCircle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const LOOKUP_LABEL = {
  found: "Found",
  not_found: "Not found",
  skipped: "Skipped",
  error: "Failed",
  not_run: "Not run",
};

function StatusRow({ label, lookup }) {
  const status = lookup?.status || "not_run";
  const tone = status === "found"
    ? "text-green-700 bg-green-50 border-green-200"
    : status === "error"
      ? "text-red-700 bg-red-50 border-red-200"
      : status === "skipped" || status === "not_run"
        ? "text-gray-600 bg-gray-50 border-gray-200"
        : "text-amber-800 bg-amber-50 border-amber-200";
  return (
    <div className={`rounded-lg border px-3 py-2 ${tone}`}>
      <div className="text-xs font-semibold uppercase tracking-wide">
        {label}: {LOOKUP_LABEL[status] || status}
      </div>
      {lookup?.message && (
        <p className="mt-1 text-sm leading-snug">{lookup.message}</p>
      )}
    </div>
  );
}

export default function UnknownMedicationDialog({
  finding,
  open,
  readOnly = false,
  applying = false,
  applyError = "",
  onApply,
  onIgnore,
  onClose,
  onRetryScan,
}) {
  const suggestions = finding?.suggestions || [];
  const lookupFailed = finding?.lookupStatus === "lookup_failed";

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose?.(); }}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle>Unknown medicine</DialogTitle>
          <DialogDescription>
            {finding?.originalText
              ? `“${finding.originalText}” is highlighted because it is not a confirmed medicine name.`
              : "Review the suggested correction."}
          </DialogDescription>
        </DialogHeader>

        {finding && (
          <div className="space-y-3">
            <StatusRow label="Medication list" lookup={finding.lookup?.database} />
            <StatusRow label="Online drug reference" lookup={finding.lookup?.online} />

            {finding.error?.message && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{finding.error.message}</span>
              </div>
            )}

            {suggestions.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-800">Suggested medicine name</p>
                <p className="text-xs text-gray-600">
                  Replace here updates this one name. Replace all in SOAP note updates that wrong name in Subjective, Objective, Assessment, and Plan, including the history and medication list.
                </p>
                {suggestions.map((suggestion) => (
                  <div
                    key={`${suggestion.source}-${suggestion.name}`}
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
                  >
                    <span className="block font-medium">{suggestion.name}</span>
                    <span className="mt-1 block text-xs text-amber-800">
                      {suggestion.groundedInTranscript
                        ? "This name appears in the doctor-patient conversation."
                        : suggestion.source === "database"
                          ? "Match from the medication list."
                          : "Match from the online drug reference."}
                    </span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={readOnly || applying}
                        onClick={() => onApply?.(suggestion, false)}
                        className="rounded-md bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Replace here
                      </button>
                      <button
                        type="button"
                        disabled={readOnly || applying}
                        onClick={() => onApply?.(suggestion, true)}
                        className="rounded-md border border-amber-700 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Replace all in SOAP note
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!suggestions.length && !lookupFailed && (
              <p className="text-sm text-gray-600">
                No corrected name is available. The note was not changed.
              </p>
            )}

            {readOnly && (
              <p className="text-sm text-gray-600">
                This note is read-only, so the suggestion cannot be applied.
              </p>
            )}

            {applying && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating this medicine name…
              </div>
            )}

            {applyError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{applyError}</span>
              </div>
            )}

            {onIgnore && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-amber-200 pt-3">
                <p className="text-xs text-amber-900">
                  Is the name correct? Keep it as written and stop highlighting it in this note.
                </p>
                <button
                  type="button"
                  disabled={applying}
                  onClick={onIgnore}
                  className="rounded-md border border-amber-300 bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-200 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Keep &amp; ignore
                </button>
              </div>
            )}

            {lookupFailed && onRetryScan && (
              <button
                type="button"
                onClick={onRetryScan}
                className="text-sm font-medium text-blue-700 hover:text-blue-900"
              >
                Check medicine names again
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
